import { FILTER_OPERATOR_ENUMS, FILTER_RULE_ENUMS } from '@constants';
import {
  formatCurrencyValue,
  formatDate,
  getFullName,
  getShortName,
  timeFromNow,
} from '@utils';
import {
  PROCUREMENT_ORDERS_FIELDS_MAPPER,
  PROCUREMENT_ORDERS_PAGE_SIZE,
  PROCUREMENT_ORDERS_DEFAULT_ORDER_BY,
  PROCUREMENT_ORDERS_DEFAULT_ORDER,
} from 'components/procurement/constants';
import { omit } from 'lodash-es';

const handleProcurementOrdersData = (data = []) => {
  if (!data) {
    return [];
  }

  return data.map((item) => ({
    ...item,
    createdAt: item?.createdAt
      ? formatDate(item?.createdAt, 'dd/MM/yyyy')
      : '-',
    timeAgo: item?.createdAt ? timeFromNow(item?.createdAt) : '-',
    stage: PROCUREMENT_ORDERS_FIELDS_MAPPER.stage.options.find(
      (stageOption) => stageOption.value === item?.stage
    ),
    totalPrice: formatCurrencyValue(item.totalPrice),
  }));
};

const handleSuppliersData = (data = []) => {
  if (!data?.length > 0) {
    return [];
  }

  return data
    ?.map((item) => ({
      text: item.name,
      value: item.slug,
    }))
    ?.sort((a, b) => a.text.localeCompare(b.text));
};

const getProcurementOrdersSortingParams = (params) => {
  const {
    page = 1,
    pageSize = PROCUREMENT_ORDERS_PAGE_SIZE,
    orderBy = PROCUREMENT_ORDERS_DEFAULT_ORDER_BY,
    order = PROCUREMENT_ORDERS_DEFAULT_ORDER,
  } = params;

  return {
    page,
    pageSize,
    orderBy,
    order,
  };
};

const handleAssigneeData = (data = []) => {
  if (!data) {
    return [];
  }

  return data
    ?.map((item) => ({
      text: item.name,
      value: item.slug,
    }))
    ?.sort((a, b) => a.text.localeCompare(b.text));
};

const handleSourcingTypes = (data = []) => {
  if (!data?.length > 0) {
    return [];
  }

  return data
    ?.map((item) => ({
      value: item,
      text: item,
    }))
    ?.sort((a, b) => a.text.localeCompare(b.text));
};

const handleProcurementOrderData = (data) => {
  return {
    ...data,
    skuGroups: data?.skuGroups?.length
      ? data.skuGroups.map((skuGroup) => ({
          ...skuGroup,
          isLocal: false,
          name: `${skuGroup?.vehicleDetails?.manufacturer || ''} ${
            skuGroup?.vehicleDetails?.model || ''
          } ${skuGroup?.vehicleDetails?.edition || ''}`.trim(),
        }))
      : [],
    createdAt: data?.createdAt
      ? formatDate(data?.createdAt, 'dd-MM-yyyy')
      : '-',
  };
};

export const handleAgentsData = (data = []) => {
  if (!data) {
    return [];
  }

  return data?.map((item) => ({
    status: item.status || '-',
    text: getFullName(item),
    display: getFullName(item),
    value: item?.email || '-',
    shortName: getShortName(item),
    createdAt: item.createdAt ? formatDate(item.createdAt, 'dd/MM/yyyy') : '-',
    updatedAt: item.updatedAt ? formatDate(item.updatedAt, 'dd/MM/yyyy') : '-',
    email: item?.email || '-',
    firstName: item?.firstName || '-',
    lastName: item?.lastName || '-',
    fullName: getFullName(item),
    role: item?.role || '-',
    id: item.id,
    userName: item.username || '-',
    isActive: item?.isActive,
    statusIsActive: item?.isActive ? 'Active' : 'Inactive',
  }));
};

const getProcurementOrdersPayload = (params = {}) => {
  const fields =
    omit(params, ['page', 'pageSize', 'order', 'orderBy', 'query']) || {};
  const numberFields = ['stage'];
  const getValueField = ({ field }) => {
    if (numberFields.includes(field)) {
      return Number(params[field]);
    }
    return params[field];
  };
  const getType = () => {
    return FILTER_RULE_ENUMS.ComparableRuleType;
  };

  const rules = [...Object.keys(fields)].map((field) => ({
    field,
    operator: FILTER_OPERATOR_ENUMS['='],
    type: getType(),
    value: getValueField({ field }),
  }));

  if (rules.length) {
    return {
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value: rules,
    };
  }

  return {
    type: FILTER_RULE_ENUMS.EmptyRuleType,
  };
};

export {
  handleProcurementOrdersData,
  getProcurementOrdersSortingParams,
  handleAssigneeData,
  handleSuppliersData,
  handleSourcingTypes,
  handleProcurementOrderData,
  getProcurementOrdersPayload,
};
